import React, {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import {LoadingButton} from '@mui/lab'
import {Grid, Typography, Box, Alert} from '@mui/material'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import {FormProvider, RHFTextField} from '../../../../common/hook-form'
import RHFTextArea from '../../../../common/hook-form/RHFTextArea'
import MUIModal from '../../../../common/MUIModal'
import {apiConstants, post} from '../../../../common/constants'
import {useSnackbar} from 'notistack'
import RHFPincodeAddressField from '../../../../common/hook-form/RHFPincodeAddressField'
import {getMerchantAddressextAPI} from '../../../../helpers/UtilityHelper'

// ── Validation schema with detailed messages ──
const validationSchema = (existingLocationNames, currentId) =>
  Yup.object().shape({
    name: Yup.string()
      .required('Location name is required')
      .min(3, 'Name must be at least 3 characters')
      .max(100, 'Name cannot exceed 100 characters')
      .test('unique-name', 'A pickup location with this name already exists. Each location must have a unique name.', function (value) {
        if (!value || currentId) return true
        return !existingLocationNames.includes(value.toLowerCase().trim())
      }),
    email: Yup.string()
      .required('Email is required for pickup notifications')
      .email('Please enter a valid email address (e.g., ops@company.com)'),
    contactNumber: Yup.string()
      .required('Contact number is required for courier coordination')
      .matches(/^[6-9][0-9]{9}$/, 'Enter a valid 10-digit Indian mobile number starting with 6-9'),
    gstin: Yup.string()
      .required('GSTIN is required for invoicing and compliance')
      .matches(
        /^[0-3][0-9][A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
        'Invalid GSTIN format. Expected: 22AAAAA0000A1Z5 (2-digit state code + 5 letters + 4 digits + 1 letter + 1 alphanumeric + Z + 1 alphanumeric)'
      ),
    address_line: Yup.string()
      .required('Full address is required')
      .min(10, 'Address must be at least 10 characters'),
    pincode: Yup.string()
      .required('PIN code is required')
      .matches(/^[1-9][0-9]{5}$/, 'Enter a valid 6-digit Indian PIN code (e.g., 110001). Must not start with 0.'),
    state: Yup.string()
      .required('State is required')
      .matches(/^[a-zA-Z\-'. ]+$/, 'State name should contain only letters'),
    city: Yup.string()
      .required('City is required')
      .matches(/^[a-zA-Z\-'. ]+$/, 'City name should contain only letters'),
  })

const FONT = 'Poppins, sans-serif'

const AddPickupLocation = ({show, handleClose, merchantId, currentId = null, setReload, shipmentType, existingLocationNames = []}) => {
  const {enqueueSnackbar} = useSnackbar()
  const [loading, setLoading] = useState(false)

  const formMethods = useForm({
    resolver: yupResolver(validationSchema(existingLocationNames, currentId)),
    mode: 'onSubmit',
    defaultValues: {name: '', email: '', contactNumber: '', gstin: '', address_line: '', pincode: '', state: '', city: ''},
  })
  const {handleSubmit, reset, watch, setValue} = formMethods

  const onSubmit = (param) => {
    setLoading(true)
    const payload = {
      // On edit, currentId is the ORIGINAL location object — spread it first so its id
      // (and any non-form fields) are retained, then let the edited form values (param)
      // override. Previously param was spread first and currentId last, which silently
      // reverted every edited field (e.g. contactNumber) back to its original value.
      ...(currentId || {}),
      ...param,
      status: 'ACTIVE',
      address_type: 'PICKUP',
      country: 'India',
      shipmentType,
    }
    post(`${apiConstants.BASE_LOCAL}${apiConstants.ADD_LOCATION}/${merchantId}`, payload)
      .then(() => {
        enqueueSnackbar('Pickup location saved successfully!', {variant: 'success'})
        setLoading(false)
        hideModal()
        setReload((r) => r + 1)
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar(err?.response?.data?.message || 'Failed to save location. Please try again.', {variant: 'error'})
        setLoading(false)
      })
  }

  const hideModal = () => {
    reset()
    handleClose()
  }

  useEffect(() => {
    if (currentId) {
      reset(currentId)
    } else {
      reset({name: '', email: '', contactNumber: '', gstin: '', address_line: '', pincode: '', state: '', city: ''})
    }
  }, [currentId, reset])

  const watchPincode = watch('pincode')
  const fetchAddresses = async () => getMerchantAddressextAPI(watchPincode)
  const handleAddressSelect = (address) => {
    setValue('city', address.city)
    setValue('state', address.state)
    setValue('pincode', address.pincode)
  }

  const isEdit = !!currentId

  return (
    <MUIModal
      open={show}
      maxWidth='md'
      title={
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
          <Box sx={{width: 36, height: 36, borderRadius: '10px', bgcolor: '#28359314', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <LocationOnOutlinedIcon sx={{color: '#283593', fontSize: 20}} />
          </Box>
          <Box>
            <Typography sx={{fontFamily: FONT, fontWeight: 700, fontSize: '1.1rem', color: '#1a1a2e'}}>
              {isEdit ? 'Edit Pickup Location' : 'Add New Pickup Location'}
            </Typography>
          </Box>
        </Box>
      }
      handleClose={hideModal}
    >
      {/* Description */}
      <Alert severity='info' icon={false} sx={{mb: 2.5, borderRadius: '10px', fontFamily: FONT, fontSize: '0.82rem', bgcolor: '#E8EAF608', border: '1px solid #E8EAF6'}}>
        <Typography sx={{fontFamily: FONT, fontSize: '0.82rem', color: '#455A64', lineHeight: 1.6}}>
          Register the warehouse or office address from which your shipments will be picked up by courier partners.
          Each pickup location must have a <strong>unique name</strong> (e.g., "Mumbai Main Warehouse", "Delhi Hub").
          The contact person at this location will receive pickup scheduling notifications.
        </Typography>
      </Alert>

      <FormProvider methods={formMethods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2.5}>

          {/* Section: Location Identity */}
          <Grid item xs={12}>
            <Typography sx={{fontFamily: FONT, fontSize: '0.78rem', fontWeight: 700, color: '#637381', textTransform: 'uppercase', letterSpacing: '0.08em', mb: -0.5}}>
              Location Identity
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <RHFTextField name='name' label='Location Name' size='medium' disabled={isEdit}
              placeholder='e.g., Mumbai Main Warehouse' />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RHFTextField name='contactNumber' label='Contact Number' size='medium'
              placeholder='10-digit mobile (e.g., 9876543210)' />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RHFTextField name='email' label='Email for Notifications' size='medium'
              placeholder='e.g., warehouse@company.com' />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RHFTextField name='gstin' label='GSTIN' size='medium'
              placeholder='e.g., 27AABCU9603R1ZM'
              inputProps={{style: {textTransform: 'uppercase'}}} />
          </Grid>

          {/* Section: Address Details */}
          <Grid item xs={12}>
            <Typography sx={{fontFamily: FONT, fontSize: '0.78rem', fontWeight: 700, color: '#637381', textTransform: 'uppercase', letterSpacing: '0.08em', mt: 1, mb: -0.5}}>
              Address Details
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <RHFTextArea name='address_line' label='Full Address'
              placeholder='Building name, street, area, landmark...' />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RHFPincodeAddressField
              name='pincode'
              label='PIN Code'
              required
              placeholder='e.g., 400001'
              fetchAddresses={fetchAddresses}
              onAddressSelect={handleAddressSelect}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RHFTextField name='city' label='City' size='medium' placeholder='e.g., Mumbai' />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RHFTextField name='state' label='State' size='medium' placeholder='e.g., Maharashtra' />
          </Grid>

          {/* Submit */}
          <Grid item xs={12} sx={{mt: 1}}>
            <LoadingButton
              type='submit'
              loading={loading}
              variant='contained'
              sx={{
                fontFamily: FONT, fontWeight: 700, fontSize: '0.9rem', textTransform: 'none',
                borderRadius: '10px', px: 4, py: 1.2,
                bgcolor: '#283593', '&:hover': {bgcolor: '#1a237e'},
              }}
            >
              {isEdit ? 'Update Location' : 'Save Pickup Location'}
            </LoadingButton>
          </Grid>
        </Grid>
      </FormProvider>
    </MUIModal>
  )
}

export default AddPickupLocation
